import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form'; 
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage'; // Updated to match resumable
import { functions, storage } from '../firebase';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { createPortal } from 'react-dom';
import '../styles/OfferForm.css';
import offerFormImage from '../assets/offer-form-image.png';





export default function OfferForm() {
  const submitForm = httpsCallable(functions, 'submitForm');
  const [selectedPhotos, setSelectedPhotos] = useState([]); // For previews
  const { register, handleSubmit, formState: { errors, isSubmitted }, reset, trigger, setValue, watch, getValues } = useForm({
  mode: 'onSubmit' // Explicitly set to only validate on submit
  });
  const auth = getAuth();
  const photos = watch('photos');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalState, setModalState] = useState('uploading'); // 'uploading' | 'success' | 'error'
  const [uploadProgress, setUploadProgress] = useState(0);
  const [modalMessage, setModalMessage] = useState('');
  const [justSubmitted, setJustSubmitted] = useState(false);
  const formRef = useRef(null);

  // Refs for all fields to sync autofill
  const fieldRefs = {
    firstName: useRef(null),
    lastName: useRef(null),
    email: useRef(null),
    phone: useRef(null),
    company: useRef(null),
    equipmentType: useRef(null),
    condition: useRef(null),
    description: useRef(null),
    city: useRef(null),
    state: useRef(null),
  };

  const submitButtonRef = useRef(null);

  useEffect(() => {
    if (!modalOpen && justSubmitted) {
      formRef.current?.scrollIntoView({ behavior: 'smooth' });
      setJustSubmitted(false);
    }
  }, [modalOpen, justSubmitted]);



  // Function to sync autofill values from DOM to form state
  const syncAutofill = () => {
    Object.keys(fieldRefs).forEach((field) => {
      const input = fieldRefs[field].current;
      if (input && input.value && input.value !== getValues(field)) {
        setValue(field, input.value, { shouldValidate: true });
      }
    });
    trigger(); // Re-validate after sync
  };

  

    useEffect(() => {
      if (photos && photos.length > 0) {
        const fileArray = Array.from(photos);
        const totalPhotos = selectedPhotos.length + fileArray.length;
        if (totalPhotos > 15 || fileArray.some(file => !file.type.startsWith('image/') || file.size > 7 * 1024 * 1024)) {
          alert('Invalid file: Maximum 15 images total, each must be image <7MB');
          setValue('photos', null);
        } else {
          setSelectedPhotos((prevPhotos) => [...prevPhotos, ...fileArray]); // Append new files to existing previews
        }
        setValue('photos', null); // Clear the file input after processing
        syncAutofill(); // Sync autofill when files change
      }
    }, [photos, setValue, getValues, trigger, selectedPhotos.length]);


    useEffect(() => {
      if (modalOpen) {
        const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = `${scrollBarWidth}px`;
      } else {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }

      return () => {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      };
    }, [modalOpen]);


    // --- Security helpers: magic-byte sniff & safe names ---
    const MAGIC = {
      jpeg: [0xFF, 0xD8, 0xFF],
      png:  [0x89, 0x50, 0x4E, 0x47],
      gif:  [0x47, 0x49, 0x46, 0x38],
    };
    async function sniffMagicBytes(file) {
      const max = 12;
      const buf = await file.slice(0, max).arrayBuffer();
      const bytes = new Uint8Array(buf);
      const startsWith = (sig) => sig.every((b, i) => bytes[i] === b);
      if (startsWith(MAGIC.jpeg)) return 'image/jpeg';
      if (startsWith(MAGIC.png))  return 'image/png';
      if (startsWith(MAGIC.gif))  return 'image/gif';
      // Rough WEBP check: RIFF .... WEBP
      if (bytes[0]===0x52 && bytes[1]===0x49 && bytes[2]===0x46 && bytes[3]===0x46 && bytes[8]===0x57 && bytes[9]===0x45 && bytes[10]===0x42 && bytes[11]===0x50) {
        return 'image/webp';
      }
      return null; // unknown
    }
    function extFromMime(m) {
      return m === 'image/jpeg' ? 'jpg'
          : m === 'image/png'  ? 'png'
          : m === 'image/gif'  ? 'gif'
          : m === 'image/webp' ? 'webp'
          : 'bin';
    }
    function safeBase(name) {
      return String(name || 'img').replace(/[^a-z0-9._-]+/gi, '_').slice(0, 60);
    }







 const onSubmit = async (data) => {
  try {
    setModalOpen(true); // Open modal at start
    setModalState('uploading'); // Start in uploading mode
    setUploadProgress(0); // Reset progress
    // Secure file upload: Validate type/size/count
    if (selectedPhotos.length > 0) { // Use selectedPhotos instead of data.photos
      console.log('Starting uploads for', selectedPhotos.length, 'files');

      // Calculate total bytes for all files
      const totalBytes = selectedPhotos.reduce((acc, file) => acc + file.size, 0);
      let transferredBytes = 0;
      const lastTransferred = new Array(selectedPhotos.length).fill(0);

      const uid = (auth.currentUser && auth.currentUser.uid) || 'anon'; // should be set by signInAnonymously
      const uploadPromises = selectedPhotos.map(async (file, index) => {
        // 1) Magic-byte sniff to prevent spoofing
        const detected = await sniffMagicBytes(file);
        if (!detected) {
          throw new Error(`File "${file.name}" is not a supported image type.`);
        }
        // 2) Ensure the stated MIME matches the detected kind (best-effort)
        if (file.type && !file.type.startsWith('image/')) {
          throw new Error(`File "${file.name}" has invalid content-type.`);
        }
        // 3) Build rules-friendly storage path + metadata
        const ext = extFromMime(detected);
        const base = safeBase(file.name.replace(/\.[^.]+$/, ''));
        const storageRef = ref(storage, `photos/${uid}/${Date.now()}_${index}_${base}.${ext}`);
        const metadata = { contentType: detected, cacheControl: 'public, max-age=3600' };

        return new Promise((resolve, reject) => {
          const uploadTask = uploadBytesResumable(storageRef, file, metadata);
          uploadTask.on('state_changed',
            (snapshot) => {
                // Add only the delta since last callback for this file
                const delta = snapshot.bytesTransferred - lastTransferred[index];
                lastTransferred[index] = snapshot.bytesTransferred;
                transferredBytes += delta;

                if (totalBytes > 0) {
                  const pct = Math.min(99, Math.round((transferredBytes / totalBytes) * 100));
                  setUploadProgress(pct);
                }
              },
            (error) => reject(error),
            () => {
              // Ensure we count any last missing bytes for this file
              const remaining = file.size - lastTransferred[index];
              if (remaining > 0) {
                lastTransferred[index] += remaining;
                transferredBytes += remaining;
                if (totalBytes > 0) {
                  const pct = Math.min(99, Math.round((transferredBytes / totalBytes) * 100));
                  setUploadProgress(pct);
                }
              }
              getDownloadURL(storageRef).then(resolve).catch(reject);
            }
          );
        });
      });
      data.photoUrls = await Promise.all(uploadPromises);
      console.log('All uploads complete, photoUrls:', data.photoUrls);
    }
    console.log('Submitting data to function:', data); // Before submitForm

    if (selectedPhotos.length === 0) {
      // Give the user feedback even when there are no files to upload
      setUploadProgress(90);
    }
    const response = await submitForm(data);
    console.log('Submit response:', response.data);

    // Drive the progress bar to 100% first, then show success
    setUploadProgress(100);
    setTimeout(() => {
      setModalState('success');
      setJustSubmitted(true);
      setSelectedPhotos([]);
      setValue('photos', null);
      reset();
    }, 150); // tiny delay so the 100% frame renders
  } catch (error) {
    console.error('Submission error:', error);
    setModalState('error');
    setModalMessage(error.message);
  }
};




    const handleDrop = (e) => {
      e.preventDefault();
      const files = e.dataTransfer.files; // Native FileList from drop
      const totalPhotos = selectedPhotos.length + files.length;
      if (totalPhotos > 15) {
        alert('Maximum 15 images allowed');
        return;
      }
      if (Array.from(files).some(file => !file.type.startsWith('image/') || file.size > 7 * 1024 * 1024)) {
        alert('Invalid file: Must be image <7MB');
        return;
      }
      setSelectedPhotos((prevPhotos) => [...prevPhotos, ...Array.from(files)]); // Append directly to previews
    };




      useEffect(() => {
      signInAnonymously(auth)
        .then(() => console.log('Anonymous auth successful'))
        .catch((error) => console.error('Anonymous auth failed:', error));

      const timer = setTimeout(() => {
        syncAutofill(); // Keep sync for autofill, but remove trigger() to avoid early validation
      }, 500);
      return () => clearTimeout(timer);
    }, [getValues, setValue]);





    useEffect(() => {
      if (modalOpen) {
        document.body.style.overflow = 'hidden'; // Lock scroll
      } else {
        document.body.style.overflow = ''; // Unlock scroll
      }
      return () => {
        document.body.style.overflow = ''; // Cleanup on unmount
      };
    }, [modalOpen]);



    

  return (
  <section id="get-started" className="offer-form-section">
    <div className="form-left">
      <img src={offerFormImage} alt="Submit equipment details" />
    </div>

    <form ref={formRef} className="form-right modern-form" onSubmit={(e) => {
      e.preventDefault();
      syncAutofill();
      handleSubmit(onSubmit)(e);
    }}>
      <h2>Tell Us About Your Equipment</h2>

      {/* Fields with validation (required, patterns) */}
      <div className="form-row">
        <div className="form-group">
          <div className="input-wrapper">
            <input id="firstName" ref={fieldRefs.firstName} {...register('firstName', { required: 'First Name required', maxLength: 50 })} placeholder=" " />
            <label htmlFor="firstName">First Name</label>
          </div>
          <span className="error">{errors.firstName && isSubmitted ? errors.firstName.message : ''}</span>
        </div>
        <div className="form-group">
          <div className="input-wrapper">
            <input id="lastName" ref={fieldRefs.lastName} {...register('lastName', { required: 'Last Name required', maxLength: 50 })} placeholder=" " />
            <label htmlFor="lastName">Last Name</label>
          </div>
          <span className="error">{errors.lastName && isSubmitted ? errors.lastName.message : ''}</span>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <div className="input-wrapper">
            <input id="email" ref={fieldRefs.email} type="email" {...register('email', { required: 'Email required', pattern: { value: /^\S+@\S+$/i, message: 'Invalid email' } })} placeholder=" " />
            <label htmlFor="email">Email</label>
          </div>
          <span className="error">{errors.email && isSubmitted ? errors.email.message : ''}</span>
        </div>
        <div className="form-group">
          <div className="input-wrapper">
            <input id="phone" ref={fieldRefs.phone} type="tel" {...register('phone', { required: 'Phone required', pattern: { value: /^[0-9+\-() ]+$/, message: 'Invalid phone' } })} placeholder=" " />
            <label htmlFor="phone">Phone</label>
          </div>
          <span className="error">{errors.phone && isSubmitted ? errors.phone.message : ''}</span>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <div className="input-wrapper">
            <input id="company" ref={fieldRefs.company} {...register('company', { required: 'Company required', maxLength: 100 })} placeholder=" " />
            <label htmlFor="company">Company Name</label>
          </div>
          <span className="error">{errors.company && isSubmitted ? errors.company.message : ''}</span>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <div className="input-wrapper">
            <select id="equipmentType" ref={fieldRefs.equipmentType} {...register('equipmentType', { required: 'Type required' })}>
              <option value="" disabled hidden></option>
              <option value="transformers">Transformers</option>
              <option value="pvf">Pipes, Valves & Fittings</option>
              <option value="electrical">Electrical Equipment</option>
              <option value="other">Other Equipment</option>
            </select>
            <label htmlFor="equipmentType">Type of Equipment</label>
          </div>
          <span className="error">{errors.equipmentType && isSubmitted ? errors.equipmentType.message : ''}</span>
        </div>

        <div className="form-group">
          <div className="input-wrapper">
            <select id="condition" ref={fieldRefs.condition} {...register('condition', { required: 'Condition required' })}>
              <option value="" disabled hidden></option>
              <option value="new">New</option>
              <option value="used_good">Used – Good</option>
              <option value="used_fair">Used – Fair</option>
              <option value="needs_repair">Needs Repair</option>
            </select>
            <label htmlFor="condition">Equipment Condition</label>
          </div>
          <span className="error">{errors.condition && isSubmitted ? errors.condition.message : ''}</span>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <div className="input-wrapper">
            <textarea id="description" ref={fieldRefs.description} rows="4" {...register('description', { required: 'Description required', maxLength: 1000 })} placeholder=" " />
            <label htmlFor="description">Description of Equipment</label>
          </div>
          <span className="error">{errors.description && isSubmitted ? errors.description.message : ''}</span>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <div className="input-wrapper">
            <input id="city" ref={fieldRefs.city} {...register('city', { required: 'City required', maxLength: 50 })} placeholder=" " />
            <label htmlFor="city">City</label>
          </div>
          <span className="error">{errors.city && isSubmitted ? errors.city.message : ''}</span>
        </div>
        <div className="form-group">
          <div className="input-wrapper">
            <input id="state" ref={fieldRefs.state} {...register('state', { required: 'State required', maxLength: 50 })} placeholder=" " />
            <label htmlFor="state">State</label>
          </div>
          <span className="error">{errors.state && isSubmitted ? errors.state.message : ''}</span>
        </div>
      </div>

     <div className="form-row photos">
        <div className="form-group">
          <input id="photos" type="file" accept="image/*" multiple {...register('photos')} hidden />
          <label htmlFor="photos" className="label">Upload Photos of Equipment (up to 15, Max file size is 7MB each, JPEG or PNG Only)</label>
          <div
            className="drop-zone"
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            style={{ border: '2px dashed #ccc', padding: '10px', textAlign: 'center' }} // Optional: Make drop area visible
          >
            <button type="button" className="file-btn" onClick={() => document.getElementById('photos').click()}>
              {selectedPhotos.length > 0 ? `${selectedPhotos.length} images selected` : 'Click or drop images here'}
            </button>
          </div>
        </div>
      </div>

      {/* Preview uploaded images */}
      {selectedPhotos.length > 0 && (
        <div className="photo-previews">
          {selectedPhotos.map((file, index) => (
            <div key={index} className="photo-preview-wrapper">
              <img src={URL.createObjectURL(file)} alt={`Preview ${index + 1}`} style={{ width: '100px', height: '100px', objectFit: 'cover', margin: '5px' }} />
              <button
                type="button"
                className="remove-photo-btn"
                onClick={() => {
                  const updatedPhotos = selectedPhotos.filter((_, i) => i !== index); // Remove from previews
                  setSelectedPhotos(updatedPhotos);
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <button ref={submitButtonRef} type="submit" className="btn-submit">Submit & Get Your Offer</button>
    </form>

    {modalOpen && createPortal(
      <div className="modal-overlay">
        <div className="modal-content-form">
          {modalState === 'uploading' ? (
            <div className="progress-container">
              <div className="circle-wrapper">
                <svg className="progress-circle" viewBox="0 0 36 36">
                  <path
                    className="circle-bg"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className="circle"
                    strokeDasharray={`${uploadProgress}, 100`}
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                </svg>
                <div className="progress-text">{Math.round(uploadProgress)}%</div>
              </div>
              <p>Uploading files...</p>
            </div>
          ) : modalState === 'success' ? (
            <>
              <h3>Form Submitted Successfully!</h3>
             <button className="modal-close-btn" onClick={() => setModalOpen(false)}>Close</button>
            </>
          ) : (
            <>
              <h3>Error</h3>
              <p>{modalMessage}</p>
              <button className="modal-close-btn" onClick={() => setModalOpen(false)}>Close</button>
            </>
          )}
        </div>
      </div>,
      document.body // Render directly to body
    )}
  </section>
);
};
